<template>
  <a-dialog
      :close-action="false"
      :size="mode.import.step > 0 && mode.import.step < 4 ? 'x-large' : 'normal'"
      ref="dialog"
      name="add_keywords"
      :loading="loading"
      :loading-progress="loadingProgress"
      @open="opened"
      @close="closed">
    <span slot="title">
      {{ $t('title') }}
    </span>
    <span slot="title-right">
      <span v-if="isFirstStep">
        <v-btn class="hidden-xs-only" text outlined @click="mode.active = mode.active === 0 ? 1 : 0">
          <v-icon v-if="mode.active === 0" left>mdi-upload</v-icon>
          <v-icon v-else left>mdi-pencil</v-icon>
          {{ mode.active === 0 ? $t('import') : $t('input') }}
        </v-btn>
        <v-btn class="hidden-sm-and-up" fab small>
          <v-icon @click="$refs.file.click()">mdi-upload</v-icon>
        </v-btn>
      </span>
    </span>
    <div slot="content" @dragenter.prevent="mode.active = 1" @dragover.prevent @drop.prevent="onDropFile">
      <div v-show="mode.active === 0">
        <div v-show="mode.write.step === 0">
          <p class="hidden-xs-only"><strong>{{ $t('drag') }}</strong>{{ $t('file') }}<strong>{{
              $t('grab')
            }}</strong>{{ $t('keywords') }}</p>
          <write-form @valid="onWriteFormValid" ref="write-form"></write-form>
        </div>
        <div v-if="mode.write.step === 1">
          <router-link
              v-if="mode.write.verify.over_quota.length > 0"
              to="account/subscriptions" target="_blank" class="text-decoration-none">
            <v-alert class="mt-4 mb-0" type="error" text border="left" icon="mdi-information-outline">
              {{ $t('alert-account') }}
              <span v-if="$account.getSubscription('check-position').name === 'default'"
                    class="text-decoration-underline">
               {{ $t('subscribe') }}
              </span>
              <span v-else class="text-decoration-underline">
               {{ $t('add') }}
              </span>
            </v-alert>
          </router-link>
          <v-alert
              v-else-if="mode.write.verify.duplicated.length > 0 || mode.write.verify.invalid.length > 0"
              class="mt-4 mb-0" type="info" text border="left" icon="mdi-information-outline">
            {{ $t('alert-invalid') }}
          </v-alert>
          <v-alert v-else class="mt-4 mb-0" type="success" text border="left" icon="mdi-check">
            {{ $t('add-success') }}
          </v-alert>
          <v-list class="no-padding">
            <v-list-item two-line v-if="mode.write.verify.over_quota.length > 0">
              <v-list-item-content>
                <v-list-item-title class="red--text">
                  {{ $t('account-capacity') }}
                  <strong>{{ mode.write.verify.over_quota.length }}</strong>
                </v-list-item-title>
                <v-list-item-subtitle>{{ mode.write.verify.over_quota.join(', ') }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item two-line v-if="mode.write.verify.valid.length > 0">
              <v-list-item-content>
                <v-list-item-title class="blue--text text--darken-2">
                  {{ $t('validated-keywords') }}<strong>{{ mode.write.verify.valid.length }}</strong>
                </v-list-item-title>
                <v-list-item-subtitle>{{ mode.write.verify.valid.join(', ') }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item two-line v-if="mode.write.verify.duplicated.length > 0">
              <v-list-item-content>
                <v-list-item-title class="grey--text">
                  {{ $t('duplicated-ignored-keywords') }}<strong>{{ mode.write.verify.duplicated.length }}</strong>
                </v-list-item-title>
                <v-list-item-subtitle>{{ mode.write.verify.duplicated.join(', ') }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item two-line v-if="mode.write.verify.invalid.length > 0">
              <v-list-item-content>
                <v-list-item-title class="grey--text">
                  {{ $t('invalid-ignored-keywords') }}<strong>{{ mode.write.verify.invalid.length }}</strong>
                </v-list-item-title>
                <v-list-item-subtitle>{{ mode.write.verify.invalid.join(', ') }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </div>
      <div v-show="mode.active === 1">
        <div class="text-center pa-12" v-show="mode.import.step === 0" style="cursor: pointer;"
             @click="$refs.file.click()">
          <v-icon class="mt-12" x-large color="grey">mdi-upload</v-icon>
          <p class="mt-4 grey--text text--darken-4">{{ $t('import') }}<br/>{{ $t('search') }}</p>
          <p class="mt-6 grey--text">{{ $t('compatible-files') }}<br/>{{ $t('files') }}</p>
          <p class="mt-6 grey--text">{{ $t('max-size') }}<strong>{{ $t('size') }}</strong>,<br/>
            {{ $t('contact') }}</p>
        </div>
        <div v-if="mode.import.step > 0">
          <import-form
              @valid="onImportFormValid"
              @step="onImportFormStep"
              :filename="mode.import.filename"
              :filetype="mode.import.filetype"
              :preset="mode.import.preset"
              ref="import-form">
          </import-form>
        </div>
      </div>
      <input type="file" ref="file" @change="mode.active = 1; next()" style="display: none">
    </div>
    <span slot="actions">
      <v-btn v-if="isFirstStep" color="grey" text @click="close()">
        {{ $t('actions.cancel') }}
      </v-btn>
      <v-btn v-else color="grey" text @click="back">
        {{ $t('actions.previous') }}
      </v-btn>

      <v-btn
          v-if="mode.active === 0 && mode.write.step === 0"
          color="primary"
          text
          :disabled="!mode.write.valid"
          @click="next">
        {{ $t('actions.next') }}
      </v-btn>
      <v-btn
          v-else-if="mode.active === 0 && mode.write.step === 1"
          color="primary"
          text
          :disabled="mode.write.verify.valid.length === 0"
          @click="submit">
        {{ $t('actions.create') }} {{ mode.write.verify.valid.length }} {{ $t('keywords') }}
      </v-btn>

      <v-btn
          v-else-if="mode.active === 1 && mode.import.step === 0"
          color="primary"
          :disabled="true"
          text>
        {{ $t('actions.next') }}
      </v-btn>
      <v-btn
          v-else-if="mode.active === 1 && mode.import.step === 4"
          color="primary"
          text
          @click="submit">{{ $t('import-keywords') }}
      </v-btn>
      <v-btn
          v-else-if="mode.active === 1 && mode.import.step > 0"
          color="primary"
          text
          :disabled="!mode.import.valid"
          @click="nextImportStep">{{ $t('actions.next') }}
      </v-btn>
    </span>
  </a-dialog>
</template>

<script>
import moment from 'moment'
import WriteForm from './Components/WriteForm'
import ImportForm from './Components/ImportForm'

export default {
  name: 'AddKeywordsDialog',
  components: { ImportForm, WriteForm },
  data () {
    return {
      loading: '',
      loadingProgress: 0,
      mode: {
        active: 0,
        write: {
          valid: false,
          step: 0,
          payload: {},
          verify: {}
        },
        import: {
          step: 0,
          valid: false,
          filename: null,
          filetype: null,
          preset: false
        }
      }
    }
  },
  computed: {
    isFirstStep () {
      if (this.mode.active === 0) {
        return this.mode.write.step === 0
      } else {
        return this.mode.import.step === 0
      }
    }
  },
  methods: {
    formatBytes (bytes, decimals = 2) {
      if (bytes === 0) return '0 Bytes'

      const k = 1024
      const dm = decimals < 0 ? 0 : decimals
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

      const i = Math.floor(Math.log(bytes) / Math.log(k))

      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
    },
    onWriteFormValid (valid) {
      this.mode.write.valid = valid
    },
    onImportFormValid (valid) {
      this.mode.import.valid = valid
    },
    onImportFormStep (step) {
      this.mode.import.step = step
    },
    nextImportStep () {
      this.$refs['import-form'].next()
    },
    async onDropFile (event) {
      this.$refs.file.files = event.dataTransfer.files
      this.next()
    },
    opened () {
      this.$nextTick(() => {
        this.$refs['write-form'].fetch()
      })
    },
    closed () {
      if (this.$refs['write-form']) this.$refs['write-form'].$reset()
      if (this.$refs['import-form']) this.$refs['import-form'].$reset()
      this.$reset()
    },
    close () {
      if (this.$refs.dialog) this.$refs.dialog.close()
    },
    back () {
      if (this.mode.active === 0) {
        return this.mode.write.step = 0
      } else {
        if (this.mode.import.step > 0) {
          this.$refs['import-form'].back()
        } else {
          this.mode.import.step = 0
        }
      }
    },
    async next () {
      if (this.mode.active === 0) {
        try {
          this.loading = this.$t('keywords-analyze')

          // Save the payload and send the verify request
          this.mode.write.payload = this.$refs['write-form'].getPayload()
          const result = await this.$api.post('monitors/verify', this.mode.write.payload)

          // Save the verify result and save the normalized website and keywords
          this.mode.write.verify = result.data.verify
          this.mode.write.payload.website = result.data.website
          this.mode.write.payload.keywords = result.data.verify.valid

          this.mode.write.step = 1
        } finally {
          this.loading = ''
        }
      } else {
        try {
          this.loading = this.$t('send-file')

          const data = new FormData()
          data.append('file', this.$refs.file.files[0])
          const start = new Date()
          const result = await this.$api.post('monitors/import', data, {
            timeout: 0,
            showLoading: false,
            onUploadProgress: (progressEvent) => {
              const seconds = (new Date() - start) / 1000
              const speed = this.formatBytes(progressEvent.loaded / seconds * 8)

              this.loadingProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              this.loading = this.$t('send-file') + this.loadingProgress + ' % ...<br /> (' + this.$t('average-speed') + ' ' + speed + '/s)'
            }
          })
          this.mode.import.filename = result.data.filename
          this.mode.import.filetype = result.data.filetype
          this.mode.import.preset = result.data.preset

          this.mode.import.step = 1
        } finally {
          this.$refs.file.value = ''
          this.loading = ''
        }
      }
    },
    async submit () {
      if (this.mode.active === 0) {
        try {
          const payload = this.mode.write.payload
          this.$autocompletion.add('domains', payload.website)

          await this.$repos.monitors.create(payload)

          this.$ui.success(this.$t('success'))
          this.$bus.emit('monitoring/require_refresh')
          this.$bus.emit('monitoring/change_date', {
            current: moment().format('YYYY-MM-DD 00:00'),
            comparison: moment().subtract(1, 'day').format('YYYY-MM-DD 00:00')
          })
          if (this.$refs.dialog) this.$refs.dialog.close()
        } catch (err) {
          if (err.exception === 'ThrottledCreateMonitorException') {
            this.$bus.emit('notification/payment_required_for_more_monitors')
          }
        } finally {
          this.loading = ''
        }
      } else {
        await this.$api.post('monitors/import/process', {
          'reading': {
            csv: this.$refs['import-form'].reading.csv.fields,
            ignore_headers: this.$refs['import-form'].reading.ignore_headers
          },
          'formatting': {
            columns: this.$refs['import-form'].formatting.columns.fields
          },
          'filling': this.$refs['import-form'].filling.fields,
          'filename': this.mode.import.filename,
          'filetype': this.mode.import.filetype,
        })

        this.$ui.success(this.$t('success-import'))
        this.$ui.openUserNotifications()
        this.close()
      }
    }
  }
}
</script>

<style>
.no-padding .v-list-item__content {
  padding-bottom: 0 !important;
}
</style>

<i18n>
{
	"en": {
		"account-capacity": "Keywords ignored because exceeding the capacity of your account :",
		"add": "Add keywords to your subscription to create more keywords.",
		"add-success": "All your keywords are ready to be created in your account.",
		"alert-account": "Some keywords are beyond the capacity of your account.",
		"alert-invalid": "You have duplicate or invalid keywords which will be ignored during creation.",
		"average-speed": "average Speed",
		"compatible-files": "The following file types are compatible :",
		"contact": "to import larger files, please contact us.",
		"drag": "Deposit ",
		"duplicated-ignored-keywords": "Duplicated and ignored keywords:",
		"error": "Error when sending your import file:",
		"file": "a file to import or ",
		"files": "xlsx, csv, tsv, ods, xls, slk, xml, gnumeric, html.",
		"grab": "enter",
		"import": "Drop a file to import",
		"import-keywords": "Import keywords",
		"input": "input mode",
		"invalid-ignored-keywords": "Invalid and ignored keywords :",
		"keywords": " keywords",
		"keywords-analyze": "Keyword Analysis ...",
		"max-size": "The maximum file size is",
		"search": "or click to browse for a file on your device.",
		"send-file": "Sending the import file ...",
		"size": "128 MB",
		"subscribe": "Subscribe to an offer to create more keywords.",
		"success": "Keywords have been added, positions are being updated.",
		"success-import": "The import was successfully scheduled, follow its progress in the notifications box.",
		"title": "Add keywords",
		"validated-keywords": "Valid keywords : "
	},
	"es": {
		"account-capacity": "Palabras clave ignoradas porque exceden la capacidad de su cuenta :",
		"add": "Añadir palabras clave a su suscripción para crear más palabras clave.",
		"add-success": "Todas sus palabras clave están listas para ser creadas en su cuenta.",
		"alert-account": "Algunas palabras clave superan la capacidad de su cuenta.",
		"alert-invalid": "Tiene palabras clave duplicadas o inválidas que se ignorarán durante la creación.",
		"average-speed": "velocidad Media",
		"compatible-files": "Los siguientes tipos de archivos son compatibles :",
		"contact": "para importar archivos más grandes, por favor contáctenos.",
		"drag": "Depositar ",
		"duplicated-ignored-keywords": "Palabras clave duplicadas y ignoradas:",
		"error": "Error al enviar su archivo de importación:",
		"file": "un archivo para importar o ",
		"files": "xlsx, csv, tsv, ods, xls, slk, xml, gnumeric, html.",
		"grab": " ingresar ",
		"import": "importar un archivo",
		"import-keywords": "Importar palabras clave",
		"input": "Modo de entrada",
		"invalid-ignored-keywords": "Palabras clave no válidas y ignoradas :",
		"keywords": "palabras clave",
		"keywords-analyze": "Análisis de palabras clave ...",
		"max-size": "El tamaño máximo de archivo es",
		"search": "o haga clic para buscar un archivo en su dispositivo.",
		"send-file": "Enviando el archivo de importación ...",
		"size": "128 MB",
		"subscribe": "Suscríbase a una oferta para crear más palabras clave.",
		"success": "Se han agregado palabras clave, las posiciones se están actualizando.",
		"success-import": "La importación se programó correctamente, siga su progreso en el cuadro de notificaciones.",
		"title": "Añadir palabras clave",
		"validated-keywords": "Palabras clave válidas : "
	},
	"fr": {
		"account-capacity": "Mots clés ignorés car dépassant la capacité de votre compte :",
		"add": "Ajoutez des mots clés à votre souscription pour créer plus de mots clés.",
		"add-success": "Tous vos mots clés sont prêt à être créés dans votre compte.",
		"alert-account": "Certains mots clés dépassent la capacité de votre compte.",
		"alert-invalid": "Vous avez des mots clés dupliqués ou invalides qui seront ignorés lors de la création.",
		"average-speed": "vitesse moyenne",
		"compatible-files": "Les types de fichiers suivants sont compatibles :",
		"contact": "pour importer de plus gros fichiers, merci de nous contacter.",
		"drag": "Déposez ",
		"duplicated-ignored-keywords": "Mots clés dupliqués et ignorés : ",
		"error": "Erreur lors de l'envoi de votre fichier d'import : ",
		"file": "un fichier à importer ou ",
		"files": "xlsx, csv, tsv, ods, xls, slk, xml, gnumeric, html.",
		"grab": "saisissez ",
		"import": "Importer un fichier",
		"import-keywords": "Importer les mots clés",
		"input": "Mode saisie",
		"invalid-ignored-keywords": "Mots clés invalides et ignorés : ",
		"keywords": "mot(s) clé(s)",
		"keywords-analyze": "Analyse des mots clés ...",
		"max-size": "La taille maximale d'un fichier est de ",
		"search": "ou cliquez pour rechercher un fichier sur votre appareil.",
		"send-file": "Envoi du fichier d'import ...",
		"size": "128 Mo",
		"subscribe": "Souscrivez à une offre pour créer plus de mots clés.",
		"success": "Les mots clés ont été ajoutés, les positions sont en cours d'actualisation.",
		"success-import": "L'import a été programmé avec succès, suivez sa progression dans l'encart de notifications.",
		"title": "Ajouter des mots clés",
		"validated-keywords": "Mots clés valides : "
	},
	"it": {
		"account-capacity": "Parole chiave ignorate perché superano la capacità del tuo account :",
		"add": "Aggiungi parole chiave al tuo abbonamento per crearne altre.",
		"add-success": "Tutte le tue parole chiave sono pronte per essere create nel tuo account.",
		"alert-account": "Alcune parole chiave superano la capacità del tuo account.",
		"alert-invalid": "Hai parole chiave duplicate o non valide che verranno ignorate durante la creazione.",
		"average-speed": "velocità media",
		"compatible-files": "I seguenti tipi di file sono compatibili :",
		"contact": "per importare file più grandi, ti preghiamo di contattarci.",
		"drag": "Deposita ",
		"duplicated-ignored-keywords": "Parole chiave duplicate e ignorate:",
		"error": "Errore durante l'invio del file di importazione:",
		"file": "un file da importare o ",
		"files": "xlsx, csv, tsv, ods, xls, slk, xml, gnumeric, html.",
		"grab": "inserisci ",
		"import": "Importa un file",
		"import-keywords": "Importa parole chiave",
		"input": "Modalità inserimento",
		"invalid-ignored-keywords": "Parole chiave non valide e ignorate :",
		"keywords": "parola/e chiave",
		"keywords-analyze": "Analisi delle parole chiave ...",
		"max-size": "La dimensione massima del file è ",
		"search": "o clicca per cercare un file sul tuo dispositivo.",
		"send-file": "Invio del file di importazione ...",
		"size": "128 MB",
		"subscribe": "Abbonati a un'offerta per creare più parole chiave.",
		"success": "Le parole chiave sono state aggiunte, le posizioni sono in fase di aggiornamento.",
		"success-import": "L'importazione è stata pianificata con successo, segui il progresso nel pannello delle notifiche.",
		"title": "Aggiungi parole chiave",
		"validated-keywords": "Parole chiave valide : "
	},
	"pt": {
		"account-capacity": "Palavras-chave ignoradas por exceder a capacidade da sua conta :",
		"add": "Adicione palavras-chave à sua subscrição para criar mais palavras-chave.",
		"add-success": "Todas as suas palavras-chave estão prontas para ser criadas na sua conta.",
		"alert-account": "Algumas palavras-chave ultrapassam a capacidade da sua conta.",
		"alert-invalid": "Tem palavras-chave duplicadas ou inválidas que serão ignoradas durante a criação.",
		"average-speed": "velocidade média",
		"compatible-files": "Os seguintes tipos de ficheiros são compatíveis :",
		"contact": "para importar ficheiros maiores, por favor contacte-nos.",
		"drag": "Deposite ",
		"duplicated-ignored-keywords": "Palavras-chave duplicadas e ignoradas:",
		"error": "Erro ao enviar o seu ficheiro de importação:",
		"file": "um ficheiro para importar ou ",
		"files": "xlsx, csv, tsv, ods, xls, slk, xml, gnumeric, html.",
		"grab": "introduza",
		"import": "Importar um ficheiro",
		"import-keywords": "Importar palavras-chave",
		"input": "modo de introdução",
		"invalid-ignored-keywords": "Palavras-chave inválidas e ignoradas :",
		"keywords": " palavras-chave",
		"keywords-analyze": "Análise de palavras-chave ...",
		"max-size": "O tamanho máximo do ficheiro é",
		"search": "ou clique para procurar um ficheiro no seu dispositivo.",
		"send-file": "A enviar o ficheiro de importação ...",
		"size": "128 MB",
		"subscribe": "Subscreva uma oferta para criar mais palavras-chave.",
		"success": "As palavras-chave foram adicionadas, as posições estão a ser atualizadas.",
		"success-import": "A importação foi agendada com sucesso, acompanhe o progresso na caixa de notificações.",
		"title": "Adicionar palavras-chave",
		"validated-keywords": "Palavras-chave válidas : "
	},
	"pl": {
		"account-capacity": "Słowa kluczowe zignorowane, ponieważ przekraczają pojemność Twojego konta :",
		"add": "Dodaj słowa kluczowe do swojej subskrypcji, aby tworzyć więcej słów kluczowych.",
		"add-success": "Wszystkie Twoje słowa kluczowe są gotowe do utworzenia na Twoim koncie.",
		"alert-account": "Niektóre słowa kluczowe przekraczają pojemność Twojego konta.",
		"alert-invalid": "Masz zduplikowane lub nieprawidłowe słowa kluczowe, które zostaną zignorowane podczas tworzenia.",
		"average-speed": "średnia prędkość",
		"compatible-files": "Zgodne są następujące typy plików :",
		"contact": "aby zaimportować większe pliki, prosimy o kontakt.",
		"drag": "Upuść ",
		"duplicated-ignored-keywords": "Zduplikowane i zignorowane słowa kluczowe:",
		"error": "Błąd podczas wysyłania pliku importu:",
		"file": "plik do importu lub ",
		"files": "xlsx, csv, tsv, ods, xls, slk, xml, gnumeric, html.",
		"grab": "wprowadź",
		"import": "Importuj plik",
		"import-keywords": "Importuj słowa kluczowe",
		"input": "tryb wprowadzania",
		"invalid-ignored-keywords": "Nieprawidłowe i zignorowane słowa kluczowe :",
		"keywords": " słowa kluczowe",
		"keywords-analyze": "Analiza słów kluczowych ...",
		"max-size": "Maksymalny rozmiar pliku to",
		"search": "lub kliknij, aby wyszukać plik na swoim urządzeniu.",
		"send-file": "Wysyłanie pliku importu ...",
		"size": "128 MB",
		"subscribe": "Zasubskrybuj ofertę, aby tworzyć więcej słów kluczowych.",
		"success": "Słowa kluczowe zostały dodane, pozycje są aktualizowane.",
		"success-import": "Import został pomyślnie zaplanowany, śledź jego postęp w panelu powiadomień.",
		"title": "Dodaj słowa kluczowe",
		"validated-keywords": "Prawidłowe słowa kluczowe : "
	}
}
</i18n>

